import { SvgScissors } from '@loomhq/lens';
import React from 'react';
import { useLayer } from 'react-laag';
import { connect, useSelector } from 'react-redux';

import {
  ROLLOUT_WEAVE,
  VARIANT,
  inactiveDefaultVariations,
} from 'constants/featureFlag';
import { VIDEO_ENTER_TRIM_MODE } from 'constants/hotkeys';
import { V4 } from 'constants/recordingVersions';
import { SHOW_NEW_TRIMMING_FTUX } from 'constants/triggers';
import {
  selectHasStarted,
  selectRecordingVersion,
} from 'selectors/videoPlayer';
import * as device from 'utilities/device';

import useFtux from 'hooks/useFtux';
import { useFeatureFlagValue } from 'hooks/featureFlag';

import TrimFtuxTooltip from './ftux-tooltip';
import CollapsibleSideButton from '../collapsible-side-button';
import { useShouldShowWaveform } from '../edit-tools-old/hooks';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import styles from './styles.module.less';

const TrimModeButton = ({
  onClick,
  enterTrimEnabled,
  isCollapsed,
  disabled,
  currentUserCanEdit,
}) => {
  const videoHasStarted = useSelector(selectHasStarted);
  const recordingVersion = useSelector(selectRecordingVersion);
  const shouldShowWaveform = useShouldShowWaveform();

  const [ftuxEnabled, endFtux] = useFtux({
    triggerKey: SHOW_NEW_TRIMMING_FTUX,
    launchDate: '2021-09-13',
    maxDuration: { weeks: 4 },
  });

  const { triggerProps, layerProps, renderLayer } = useLayer({
    isOpen: ftuxEnabled,
    placement: 'left-start',
    triggerOffset: 20,
    auto: true,
  });

  const weaveFlagValue = useFeatureFlagValue(ROLLOUT_WEAVE);
  const rolloutWeave = VARIANT.includes(weaveFlagValue);

  // NOTE(weave): temporarily enabling this if you are using Weave
  enterTrimEnabled = enterTrimEnabled || rolloutWeave;

  const userCanEdit = currentUserCanEdit;

  const showFtux = ftuxEnabled && !videoHasStarted && shouldShowWaveform;

  if (ftuxEnabled) {
    const _oldOnClick = onClick;

    onClick = evt => {
      endFtux();
      _oldOnClick(evt);
    };
  }

  return (
    !device.isMobile &&
    userCanEdit && (
      <CollapsibleSideButton
        className={showFtux ? styles.hasFtux : ''}
        icon={<SvgScissors />}
        isCollapsed={isCollapsed}
        isDisabled={disabled || !enterTrimEnabled}
        onClick={enterTrimEnabled ? onClick : undefined}
      >
        <div {...triggerProps} dir="ltr">
          {renderLayer(
            <div
              className={`tooltip theme-dark ${styles.ftux}`}
              {...layerProps}
            >
              <TrimFtuxTooltip show={showFtux} dismissTooltip={endFtux} />
            </div>
          )}
          {/** Exclude the V4 videos */}
          {recordingVersion === V4 ? (
            <>Trim video ({VIDEO_ENTER_TRIM_MODE[0]})</>
          ) : (
            <>
              <FeatureFlaggedComponent
                flag={ROLLOUT_WEAVE}
                activationValues={inactiveDefaultVariations}
              >
                Trim video ({VIDEO_ENTER_TRIM_MODE[0]})
              </FeatureFlaggedComponent>
              <FeatureFlaggedComponent
                flag={ROLLOUT_WEAVE}
                activationValues={VARIANT}
              >
                Trim and add clips ({VIDEO_ENTER_TRIM_MODE[0]})
              </FeatureFlaggedComponent>
            </>
          )}
        </div>
      </CollapsibleSideButton>
    )
  );
};

const mapStateToProps = state => {
  const { player_ready, video } = state.share_video;

  const { processing_information = {} } = video;
  const { trim_id, trim_progress } = processing_information;

  // the user should be able to enter trim mode if the player is ready or if
  // a trim job is progressing
  const enterTrimEnabled =
    player_ready || (trim_id != null && trim_progress !== 100);

  return {
    enterTrimEnabled,
    currentUserCanEdit: video.currentUserCanEdit,
  };
};

export default connect(mapStateToProps)(TrimModeButton);
